import { useState, useRef, useEffect } from 'react';

export default function TaskModal({ task, onSave, onSaveAndAddAnother, onClose, onDelete, folders, currentFolderId, tags, bulkCount = 1 }) {
  const isBulk = bulkCount > 1;
  const getInitialForm = () => ({
    name: task?.name || '',
    description: task?.description || '',
    start_date: task?.start_date ? task.start_date.slice(0, 10) : '',
    end_date: task?.end_date ? task.end_date.slice(0, 10) : '',
    priority: task?.priority || 'Medium',
    status: task?.status || 'Not Started',
    folder_id: task?.folder_id || currentFolderId || '',
    tag_ids: task?.tags ? task.tags.map((t) => t.id) : [],
  });

  const [form, setForm] = useState(getInitialForm);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const buildFolderOptions = (items, parentId = null, depth = 0) => {
    return items
      .filter((f) => f.parent_id === parentId)
      .flatMap((f) => [
        { id: f.id, name: f.name, label: '\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0'.repeat(depth) + f.name },
        ...buildFolderOptions(items, f.id, depth + 1),
      ]);
  };
  const folderOptions = folders ? buildFolderOptions(folders) : [];
  const [folderDropdownOpen, setFolderDropdownOpen] = useState(false);
  const folderDropdownRef = useRef(null);
  const selectedFolderName = folderOptions.find((f) => f.id === form.folder_id)?.name || '';

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (folderDropdownRef.current && !folderDropdownRef.current.contains(e.target)) {
        setFolderDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleTag = (tagId) => {
    setForm((prev) => ({
      ...prev,
      tag_ids: prev.tag_ids.includes(tagId)
        ? prev.tag_ids.filter((id) => id !== tagId)
        : [...prev.tag_ids, tagId],
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isBulk || form.name.trim()) onSave(form);
  };

  const handleSaveAndAddAnother = () => {
    if (form.name.trim() && onSaveAndAddAnother) {
      onSaveAndAddAnother(form);
      setForm({
        name: '',
        description: '',
        start_date: '',
        end_date: '',
        priority: 'Medium',
        status: 'Not Started',
        folder_id: form.folder_id,
        tag_ids: [],
      });
    }
  };

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <form className="modal-content" onSubmit={handleSubmit}>
          <div className="modal-header">
            <h5 className="modal-title">
              {task?.id ? (isBulk ? `Edit ${bulkCount} Tasks` : 'Edit Task') : 'New Task'}
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            {folders && folders.length > 0 && (
              <div className="mb-3" ref={folderDropdownRef}>
                <label className="form-label">Folder</label>
                <div
                  className="form-select"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setFolderDropdownOpen(!folderDropdownOpen)}
                >
                  {selectedFolderName || 'Select folder'}
                </div>
                {folderDropdownOpen && (
                  <ul className="dropdown-menu show w-100" style={{ maxHeight: 200, overflowY: 'auto' }}>
                    {folderOptions.map((f) => (
                      <li key={f.id}>
                        <button
                          type="button"
                          className={`dropdown-item${f.id === form.folder_id ? ' active' : ''}`}
                          onClick={() => {
                            setForm({ ...form, folder_id: f.id });
                            setFolderDropdownOpen(false);
                          }}
                          dangerouslySetInnerHTML={{ __html: f.label.replace(/\u00A0/g, '&nbsp;') }}
                        />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {!isBulk && (
              <>
                <div className="mb-3">
                  <label className="form-label">Name</label>
                  <input className="form-control" name="name" value={form.name} onChange={handleChange} required autoFocus />
                </div>
                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <textarea className="form-control" name="description" rows="2" value={form.description} onChange={handleChange} />
                </div>
              </>
            )}
            <div className="row mb-3">
              <div className="col">
                <label className="form-label">Start Date</label>
                <input type="date" className="form-control" name="start_date" value={form.start_date} onChange={handleChange} />
              </div>
              <div className="col">
                <label className="form-label">End Date</label>
                <input type="date" className="form-control" name="end_date" value={form.end_date} onChange={handleChange} />
              </div>
            </div>
            <div className="row mb-3">
              <div className="col">
                <label className="form-label">Priority</label>
                <select className="form-select" name="priority" value={form.priority} onChange={handleChange}>
                  <option>Low</option>
                  <option>Medium</option>
                  <option>High</option>
                </select>
              </div>
              <div className="col">
                <label className="form-label">Status</label>
                <select className="form-select" name="status" value={form.status} onChange={handleChange}>
                  <option>Not Started</option>
                  <option>Preparing</option>
                  <option>In Progress</option>
                  <option>On Hold</option>
                  <option>Completed</option>
                </select>
              </div>
            </div>
            {tags && tags.length > 0 && (
              <div className="mb-3">
                <label className="form-label">Tags</label>
                <div className="d-flex flex-wrap gap-2">
                  {tags.map((tag) => {
                    const selected = form.tag_ids.includes(tag.id);
                    return (
                      <span
                        key={tag.id}
                        className="badge"
                        style={{
                          backgroundColor: selected ? tag.color : 'transparent',
                          color: selected ? '#fff' : tag.color,
                          border: `2px solid ${tag.color}`,
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                        }}
                        onClick={() => toggleTag(tag.id)}
                      >
                        {tag.name}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer d-flex justify-content-between">
            <div>
              {task?.id && onDelete && (
                <button type="button" className="btn btn-outline-danger" onClick={() => onDelete(task.id)}>Delete</button>
              )}
            </div>
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              {!task?.id && onSaveAndAddAnother && (
                <button type="button" className="btn btn-outline-primary" onClick={handleSaveAndAddAnother} disabled={!form.name.trim() || !form.folder_id}>Save & Add Another</button>
              )}
              <button type="submit" className="btn btn-primary" disabled={(!isBulk && !form.name.trim()) || !form.folder_id}>Save</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
